import { diffWordsWithSpace } from 'diff';
import { DiffDOM } from 'diff-dom';

/**
 * This is used to transform 2 consecutive paragraphs into 1 such that the diff
 * calculator treats them as one element
 */
const consecutiveParagraphsSeparator = '\u200B';
/**
 * If the `consecutiveParagraphsSeparator` was wrapped in an `ins` or `del` tag,
 * when replacing back to `</p><p>` we need to insert something in between to be
 * able to show that there was a new line difference. sometimes HTML replaces
 * that character with `&ZeroWidthSpace;`, so in this regex we take in
 * consideration all the possible variants of that character
 */
const modifiedConsecutiveParagraphsSeparatorRegex = new RegExp(
  `<(ins|del)>(&ZeroWidthSpace;|&#8203;|&#x200B;|${consecutiveParagraphsSeparator})<\\/\\1>`,
);

type Diff = {
  action: string;
  route: number[];
  /** Which attribute an `*Attribute` action is about. */
  name?: string;
  oldValue?: string;
  newValue?: string;
  element?: {
    nodeName: string;
  };
};

function isDiff(obj: unknown): obj is Diff[] {
  return Array.isArray(obj) && obj.every(isDiffItem);
}

function isDiffItem(element: unknown): element is Diff {
  return (
    typeof element === 'object' &&
    element !== null &&
    'action' in element &&
    'route' in element &&
    typeof element.action === 'string' &&
    Array.isArray(element.route) &&
    element.route.every((item: unknown) => typeof item === 'number')
  );
}

const diffDom = new DiffDOM();

function getFirstTableAncestor(node: Node | null): Node | null {
  let current = node;
  while (current) {
    if (current.nodeName.toLowerCase() === 'table') return current;
    current = (current as Element).parentElement;
  }
  return null;
}

function wrapNode(node: Node, tag: string): void {
  const wrapper = document.createElement(tag);
  node.parentNode?.replaceChild(wrapper, node);
  wrapper.appendChild(node);
}

function getHtmlFragmentFromTextDiff(
  diff: Diff,
  tag: string,
): Node | undefined {
  if (!diff.oldValue || !diff.newValue) {
    return;
  }

  const textDiff = diffWordsWithSpace(diff.oldValue, diff.newValue);
  /**
   * To group multiple text elements we can use a fragment that does not create
   * a new HTML element when appended as a child
   */
  const fragment = document.createDocumentFragment();
  textDiff.forEach((part) => {
    /**
     * Because we have two views, we don't need to show the removed parts,
     * because they will be shown in the other view
     */
    if (part.removed) {
      return;
    }
    const textNode = document.createTextNode(part.value);
    /**
     * If there is no `added` attribute and no `removed` (we already checked
     * this above), there were no text changes, so need to wrap the text
     */
    if (!part.added) {
      fragment.appendChild(textNode);
      return;
    }
    /**
     * We treat 2 consecutive paragraphs as one, and mark the position between
     * them with an invisible character. If this character is part of a diff, we
     * want to wrap it isolated, so we split by it (keeping the character in the
     * split result), and wrap each part individually
     */
    const innerTextParts = textNode.wholeText
      .split(new RegExp(`(${consecutiveParagraphsSeparator})`))
      .filter((s) => s.length > 0);
    /**
     * If we found maximum one part when splitting, it means that there was no
     * invisible character, so we wrap the whole text
     */
    if (innerTextParts.length <= 1) {
      fragment.appendChild(textNode);
      wrapNode(textNode, tag);
      return;
    }
    /** Wrap each part of the split separately */
    const innerFragment = document.createDocumentFragment();
    innerTextParts.forEach((textPart) => {
      const innerTextNode = document.createTextNode(textPart);
      innerFragment.appendChild(innerTextNode);
      wrapNode(innerTextNode, tag);
    });
    fragment.appendChild(innerFragment);
  });
  return fragment;
}

function getNodeAtRoute(root: Node, route: number[]): Node | null {
  return route.reduce(
    (node, index) => node?.childNodes.item(index) ?? null,
    root,
  );
}

/**
 * The diff dom library is not very good at detecting precise changes in a
 * table, so everytime there is a change in a table, we mark the whole table as
 * changed
 */
function wrappedTableAncestor(node: Node, tag: string): boolean {
  const tableAncestor = getFirstTableAncestor(node);
  const alreadyWrapped =
    tableAncestor?.parentElement?.nodeName.toLowerCase() === tag;
  if (alreadyWrapped) {
    return true;
  }
  if (tableAncestor) {
    wrapNode(tableAncestor, tag);
    return true;
  }
  return false;
}

function applyMarkers(
  root: HTMLDivElement,
  diffs: Diff[],
  tag: 'ins' | 'del',
): HTMLDivElement {
  for (const diff of diffs) {
    const affectedElement = getNodeAtRoute(root, diff.route);
    if (!affectedElement) {
      continue;
    }
    if (wrappedTableAncestor(affectedElement, tag)) {
      continue;
    }

    switch (diff.action) {
      case 'addElement':
      case 'replaceElement':
      case 'modifyElement': {
        wrapNode(affectedElement, tag);
        break;
      }

      case 'addAttribute':
      case 'modifyAttribute':
      case 'replaceAttribute': {
        // An element id is an anchor, not content. The two sides of a
        // comparison routinely carry different ones — an AI draft keeps the
        // article's ids only on the parts it left alone — and marking the
        // element for it would report prose as rewritten that is identical.
        // `removeAttribute` is absent from this switch and so already ignored,
        // which is what made a dropped id harmless but a changed one not.
        if (diff.name === 'id') break;

        wrapNode(affectedElement, tag);
        break;
      }

      /**
       * The diff-dom library does text diff per block and not per word, so if
       * any character in the text block is different, it will mark the whole
       * block as changed. We want to have diff per word, so we are using a
       * custom-made diff for text nodes, that will wrap only the changed words
       * with the tag, and not the whole block
       */
      case 'addTextElement':
      case 'replaceTextElement':
      case 'modifyTextElement': {
        const fragment = getHtmlFragmentFromTextDiff(diff, tag);
        if (fragment) {
          affectedElement.parentNode?.replaceChild(fragment, affectedElement);
        }
        break;
      }

      default:
        break;
    }
  }

  return root;
}

/**
 * The diff is done per HTML element, and an end line in HTML is <br />, which
 * is an HTML element. This makes it more difficult to compare text because it
 * splits them in multiple text elements. Because of this we convert it to a \n,
 * and, at the end, before returning the HTML, we convert it back.
 *
 * The same is true for 2 consecutive paragraphs. We replace such blocks with a
 * special invisible character that most likely will not be used, and after
 * calculating the diff we replace it back
 */
function replaceOriginalEndLines(html: string): string {
  return html
    .replaceAll('<br />', '\n')
    .replace(/<\/p><p(?:\s[^>]*)?>/g, consecutiveParagraphsSeparator);
}

function replaceModifiedEndLines(html: string): string {
  return (
    html
      .replaceAll('\n', '<br />')
      /**
       * If the paragraphs separator was marked as added or removed, when
       * replacing back to two paragraphs, we need to add a new element between
       * them to mark the changed new line
       */
      .replace(
        modifiedConsecutiveParagraphsSeparatorRegex,
        '</p><$1><span class="newLine" /></$1><p>',
      )
      .replaceAll(consecutiveParagraphsSeparator, '</p><p>')
  );
}

export function computeDiffHtml(
  oldHtml: string,
  newHtml: string,
): { newMarked: string; oldMarked: string } {
  /**
   * We need to wrap the HTMLs into a div so that the diff works. It doesn't
   * work very well when given an HTML with multiple root children.
   */
  const wrappedOld = document.createElement('div');
  wrappedOld.innerHTML = replaceOriginalEndLines(oldHtml);
  const wrappedNew = document.createElement('div');
  wrappedNew.innerHTML = replaceOriginalEndLines(newHtml);

  const diffsOldToNew = diffDom.diff(wrappedNew, wrappedOld);
  const diffsNewToOld = diffDom.diff(wrappedOld, wrappedNew);

  if (!isDiff(diffsOldToNew) || !isDiff(diffsNewToOld)) {
    /**
     * The internal types from diff dom don't expose the attributes we need to
     * calculate the diff, so we defined our own types. If diff dom change their
     * internals types, we will just log an error and return the original HTML
     * without markers, to avoid breaking the app.
     */
    // eslint-disable-next-line no-console
    console.error('DiffDom type is not known', {
      diffsOldToNew,
      diffsNewToOld,
    });
    return { newMarked: newHtml, oldMarked: oldHtml };
  }

  const newWrappedMarked = applyMarkers(wrappedNew, diffsNewToOld, 'ins');
  const oldWrappedMarked = applyMarkers(wrappedOld, diffsOldToNew, 'del');

  const newMarked = replaceModifiedEndLines(newWrappedMarked.innerHTML);
  const oldMarked = replaceModifiedEndLines(oldWrappedMarked.innerHTML);
  return { newMarked, oldMarked };
}
